import type { AppearanceThumbnailCommand, AppearanceThumbnailMessage, AppearanceThumbnailRequest } from '@sorrem-client/game/character-preview/appearance-thumbnail-protocol';
import { createLogger, isBodyPartCompatible, type BodyPart, type BodyPartModifier } from '@sorrem/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import AppearanceThumbnailWorker from './appearance-thumbnail-worker-entry?worker';

const logger = createLogger('client:use-appearance-thumbnails');

/** The `thumbnails` key for the bare head, shown on an optional slot's "None" choice. */
export const NO_BODY_PART_THUMBNAIL_ID = '';

/** One picker tile's image. */
export interface AppearanceThumbnail {
    /** The image to show: the current one, or while that renders, the tile's previous one. `undefined` before the tile has any. */
    readonly url: string | undefined;
    /** Whether the current image is still on its way (the shown one, if any, is out of date). */
    readonly isLoading: boolean;
}

function partFitsSlot(part: BodyPart, slots: readonly string[], characterModelId: string): boolean {
    return slots.some((slot) => isBodyPartCompatible(part, slot, characterModelId));
}

function thumbnailKey(characterModelId: string, bodyPartId: string, modifiersKey: string): string {
    return `${characterModelId}|${bodyPartId}|${modifiersKey}`;
}

/**
 * Rendered head-and-shoulders images for every body part in `slots` that fits
 * `characterModelId`, in the given hair color (`outfitModifiers`), plus the
 * bare head under `NO_BODY_PART_THUMBNAIL_ID`. Returns each tile's image and
 * loading state, keyed by body part id.
 *
 * The images come from one background worker (see
 * `appearance-thumbnail-renderer.ts`) that lives as long as the caller is
 * mounted, and which also keeps finished images between visits. Each image
 * is kept for the session, so switching back to an earlier body or color is
 * instant. While a new color renders, each tile
 * keeps showing its previous image rather than going blank.
 */
export function useAppearanceThumbnails(
    bodyParts: readonly BodyPart[],
    characterModelId: string,
    slots: readonly string[],
    outfitModifiers: Partial<Record<string, BodyPartModifier>>,
): Readonly<Record<string, AppearanceThumbnail>> {
    const workerRef = useRef<Worker | null>(null);
    /** Object URL per finished snapshot; `null` for one that failed, so it isn't asked for again. */
    const [urlsByKey, setUrlsByKey] = useState<Readonly<Record<string, string | null>>>({});
    const urlsByKeyRef = useRef(urlsByKey);
    urlsByKeyRef.current = urlsByKey;
    /** The image each tile showed last, so it can stay up while its next one renders. */
    const lastShownRef = useRef(new Map<string, string>());

    const slotsKey = slots.join('|');
    const modifiersKey = JSON.stringify(outfitModifiers);

    useEffect(() => {
        const worker = new AppearanceThumbnailWorker();
        workerRef.current = worker;
        const createdUrls: string[] = [];
        const lastShown = lastShownRef.current;

        worker.onmessage = (event: MessageEvent<AppearanceThumbnailMessage>): void => {
            const message = event.data;
            if (message.type === 'thumbnail-error') {
                logger.warn(`thumbnail "${message.key}" failed: ${message.message}`);
                setUrlsByKey((previous) => ({ ...previous, [message.key]: null }));
                return;
            }
            const url = URL.createObjectURL(message.image);
            createdUrls.push(url);
            setUrlsByKey((previous) => ({ ...previous, [message.key]: url }));
        };

        return (): void => {
            worker.terminate();
            workerRef.current = null;
            createdUrls.forEach((url) => URL.revokeObjectURL(url));
            lastShown.clear();
            setUrlsByKey({});
        };
    }, []);

    const bodyPartIds = useMemo(
        () => [
            ...bodyParts
                .filter((part) => partFitsSlot(part, slotsKey.split('|'), characterModelId))
                .map((part) => part.id),
            NO_BODY_PART_THUMBNAIL_ID,
        ],
        [bodyParts, slotsKey, characterModelId],
    );

    // Ask for whatever isn't rendered yet. Replaces the worker's queue, so a
    // body or color the player has already moved past stops rendering.
    useEffect(() => {
        const worker = workerRef.current;
        if (!worker) return;

        const parsedModifiers = JSON.parse(modifiersKey) as Partial<Record<string, BodyPartModifier>>;
        const requests: AppearanceThumbnailRequest[] = bodyPartIds
            .map((bodyPartId) => ({
                key: thumbnailKey(characterModelId, bodyPartId, modifiersKey),
                characterModelId,
                bodyPartId: bodyPartId === NO_BODY_PART_THUMBNAIL_ID ? null : bodyPartId,
                outfitModifiers: parsedModifiers,
            }))
            .filter((request) => urlsByKeyRef.current[request.key] === undefined);

        const command: AppearanceThumbnailCommand = { type: 'render-thumbnails', requests };
        worker.postMessage(command);
    }, [bodyPartIds, characterModelId, modifiersKey]);

    return useMemo(() => {
        const thumbnails: Record<string, AppearanceThumbnail> = {};
        for (const bodyPartId of bodyPartIds) {
            const current = urlsByKey[thumbnailKey(characterModelId, bodyPartId, modifiersKey)];
            const tileKey = `${characterModelId}|${bodyPartId}`;
            if (current) {
                lastShownRef.current.set(tileKey, current);
            }
            thumbnails[bodyPartId] = {
                url: current ?? lastShownRef.current.get(tileKey),
                isLoading: current === undefined,
            };
        }
        return thumbnails;
    }, [bodyPartIds, characterModelId, modifiersKey, urlsByKey]);
}
